// The pipe size on an end of a component, without redrawing it (owner, 2026-10-20: "I need the pump components updated
// so I can set inlet and outlet pipe sizes").
//
// 3.15.6 (owner, 2026-10-20: "the pump sizes, when I change them, did not change those values"): a PUMP is drawn again
// at the new size (pumpRedraw.ts): its flange, bolt circle, neck and label follow the end. A pump that was ungrouped or
// edited is not drawn again; only its end changes, and the result says so. Any other fitting keeps its drawing. The END
// (meta.ports) carries the size of the pipe that goes on it: the Pipe tool draws that size from it, the pipe end menu offers fittings
// of that size, and Extend makes a pipe of that size. With `withPipes`, the pipes already on the end, and the pipes
// joined to them up to the next fitting or pump, take the new size. One undo step.
import type { Doc } from "../doc/entities";
import { applyCommand, type Command } from "../doc/commands";
import { pipeMetaOf, setPipePatches } from "./api";
import { rawPorts } from "./carry";
import { runFrom } from "./resizeFitting";
import { redrawPumpCommand } from "./pumpRedraw";
import { drawnSizes, type PumpInfo } from "../library/pumps";

export function setPortSizeCommand(
  doc: Doc,
  ownerId: string,
  index: number,
  size: number,
  withPipes = true
): { cmd: Command; pipesChanged: number; note?: string } | string {
  const g = doc.entities[ownerId];
  const ports = rawPorts(g);
  if (!g || g.type !== "group" || !ports || !ports[index]) return "That end is not on this object.";
  if (!Number.isFinite(size) || size <= 0) return "Choose a size.";
  if (ports[index]!.size === size) return "The end already has that size.";
  const next = ports.map((p, i) => (i === index ? { ...p, size } : { ...p }));
  let first: Command = { kind: "updateEntities", patches: [{ id: ownerId, before: { meta: structuredClone(g.meta) }, after: { meta: { ...(g.meta ?? {}), ports: next } } }] };
  let note: string | undefined;
  let redrawn = false;
  const pump = g.meta?.pump as PumpInfo | undefined;
  if (pump && index <= 1) {
    // the other end keeps the size it has (before 3.15.6 an end size could differ from its drawn flange)
    const cur = drawnSizes(pump);
    const inSize = index === 0 ? size : (ports[0]?.size ?? cur.dnIn);
    const outSize = index === 1 ? size : (ports[1]?.size ?? cur.dnOut);
    const redraw = redrawPumpCommand(doc, ownerId, inSize, outSize);
    if (typeof redraw === "string") note = redraw;
    else {
      first = redraw.cmd;
      redrawn = true;
    }
  }
  const commands: Command[] = [first];
  let pipesChanged = 0;
  if (withPipes) {
    const draft = structuredClone(doc);
    applyCommand(draft, commands[0]!);
    const done = new Set<string>();
    for (const e of Object.values(draft.entities)) {
      const pm = pipeMetaOf(e);
      if (!pm || done.has(e.id)) continue;
      if (![pm.a, pm.b].some((a) => a.kind === "port" && a.ownerId === ownerId && a.port === index)) continue;
      for (const pid of runFrom(draft, e.id)) {
        if (done.has(pid)) continue;
        done.add(pid);
        const cur = pipeMetaOf(draft.entities[pid]);
        if (!cur || cur.size === size) continue;
        const ps = setPipePatches(draft, pid, { size });
        if (!ps.length) continue;
        const cmd: Command = { kind: "updateEntities", patches: ps };
        applyCommand(draft, cmd);
        commands.push(cmd);
        pipesChanged++;
      }
    }
  }
  // a redraw holds a delete step: in a batch of its own, so the app does not report it as a delete
  const cmd: Command = commands.length === 1 && !redrawn ? commands[0]! : { kind: "batch", label: "end size", commands };
  return { cmd, pipesChanged, ...(note ? { note } : {}) };
}
